import { toaster } from '@/components/ui/toaster';
import { announcementsQueryKey } from '@/hooks/queries/use-announcements-query';
import api from '@/libs/axiosInstance';
import {
  dashboardAnnouncementDismissApiPath,
  type Announcement,
  type ApiResponse,
  type ProductAnnouncementDismissal,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

interface DismissProductAnnouncementContext {
  previous: Announcement[] | undefined;
}

async function dismissProductAnnouncement(
  id: string
): Promise<ApiResponse<ProductAnnouncementDismissal>> {
  const response = await api.post<ApiResponse<ProductAnnouncementDismissal>>(
    dashboardAnnouncementDismissApiPath(id)
  );
  return response.data;
}

/** Optimistically advances the panel while preserving an exact brand rollback. */
export function useDismissProductAnnouncementAction(brandId: string | null) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const queryKey = announcementsQueryKey(brandId);

  return useMutation<
    ApiResponse<ProductAnnouncementDismissal>,
    AxiosError<ApiResponse<ProductAnnouncementDismissal>>,
    string,
    DismissProductAnnouncementContext
  >({
    mutationFn: dismissProductAnnouncement,
    onMutate: async (announcementId) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Announcement[]>(queryKey);
      queryClient.setQueryData<Announcement[]>(queryKey, (current) =>
        current?.filter((announcement) => announcement.id !== announcementId)
      );
      return { previous };
    },
    onError: (_error, _announcementId, context) => {
      queryClient.setQueryData(queryKey, context?.previous);
      toaster.error({
        title: t('common.error'),
        description: t('announcements.product.dismissError'),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
